import { BufferAttribute, BufferGeometry, FrontSide, Mesh, MeshLambertMaterial, Texture } from "three";
import type { Bbox2 } from "../geo/bbox";
import type { SceneOrigin } from "../geo/origin";
import type { HeightGrid } from "../io/heights";
import { textureBytes } from "./textures";

export type TerrainMaterial = MeshLambertMaterial;

/** Appearance shared by every tile; each tile still gets its own material instance. */
export interface TerrainMaterialOptions {
  opacity: number;
  wireframe: boolean;
}

/** UV rectangle a tile occupies inside a texture that covers a larger area. */
export interface UvRect {
  offsetX: number;
  offsetY: number;
  repeatX: number;
  repeatY: number;
}

export const FULL_UV: UvRect = { offsetX: 0, offsetY: 0, repeatX: 1, repeatY: 1 };

/**
 * One cell of the terrain grid: a height-field patch with a texture that is swapped for a
 * sharper one as the camera approaches.
 */
export class TerrainTile {
  readonly mesh: Mesh<BufferGeometry, TerrainMaterial>;
  /** Raster level currently displayed; -1 means the shared low-resolution base texture. */
  textureLevel = -1;
  textureSize = 0;
  textureCost = 0;
  /** Timestamp of the last frame in which this tile was visible, for LRU eviction. */
  lastVisible = 0;
  /** Approximate height of the tile in device pixels, drives the texture level request. */
  screenSize = 0;
  loading = false;
  abort: AbortController | null = null;

  private ownTexture: Texture | null = null;
  private baseTexture: Texture | null = null;
  /**
   * Real elevations sampled from the surface model, kept only to answer height queries
   * (cursor readout, measurement tool) — the visual mesh stays flat regardless of this.
   */
  private heightGrid: HeightGrid | null = null;

  /**
   * Each tile owns its material rather than sharing one.
   *
   * `map`, `offset` and `repeat` all live on the texture, but the *binding* of a texture
   * lives on the material — with a shared material every tile would display whichever
   * texture arrived last.
   */
  constructor(
    readonly col: number,
    readonly row: number,
    readonly bbox: Bbox2,
    appearance: TerrainMaterialOptions,
  ) {
    const material = new MeshLambertMaterial({
      side: FrontSide,
      // Orthophotos carry an alpha band that is zero outside the flown area; discarding
      // those fragments keeps the mosaic's real outline instead of a black rectangle.
      alphaTest: 0.35,
      transparent: appearance.opacity < 1,
      opacity: appearance.opacity,
      wireframe: appearance.wireframe,
    });
    this.mesh = new Mesh(new BufferGeometry(), material);
    this.mesh.frustumCulled = true;
    this.mesh.matrixAutoUpdate = false;
    this.mesh.updateMatrix();
  }

  get key(): string {
    return `${this.col}/${this.row}`;
  }

  /**
   * Builds the patch geometry as a flat plate at `height`.
   *
   * The orthophoto (or hillshaded elevation ramp) is draped without following real relief —
   * a flat surface reads as a plan view and never fights the swisstopo backdrop for depth —
   * so unlike a height-fielded mesh this never needs to change once built. Areas with no
   * imagery or no elevation data are left to the material's alpha test rather than cut as
   * holes here.
   */
  buildGeometry(origin: SceneOrigin, height: number, cells: number): void {
    const points = cells + 1;
    const positions = new Float32Array(points * points * 3);
    const uvs = new Float32Array(points * points * 2);

    const { minX, minY, maxX, maxY } = this.bbox;
    const sceneHeight = origin.sceneY(height);

    for (let row = 0; row < points; row++) {
      const north = maxY - ((maxY - minY) * row) / cells;
      for (let col = 0; col < points; col++) {
        const east = minX + ((maxX - minX) * col) / cells;
        const index = row * points + col;

        positions[index * 3] = origin.sceneX(east);
        positions[index * 3 + 1] = sceneHeight;
        positions[index * 3 + 2] = origin.sceneZ(north);

        uvs[index * 2] = col / cells;
        // Texture V runs bottom-up while the grid runs north-down.
        uvs[index * 2 + 1] = 1 - row / cells;
      }
    }

    const indices: number[] = [];
    for (let row = 0; row < cells; row++) {
      for (let col = 0; col < cells; col++) {
        const a = row * points + col;
        const b = a + 1;
        const c = a + points;
        const d = c + 1;
        indices.push(a, c, b, b, c, d);
      }
    }

    const geometry = this.mesh.geometry;
    geometry.setAttribute("position", new BufferAttribute(positions, 3));
    geometry.setAttribute("uv", new BufferAttribute(uvs, 2));
    geometry.setIndex(indices.length > 65535 ? new BufferAttribute(new Uint32Array(indices), 1) : indices);
    // A flat plate always faces straight up; no need to compute per-vertex normals.
    geometry.computeBoundingSphere();
    geometry.computeBoundingBox();
  }

  /**
   * Stores the real elevations sampled for this tile, used only to answer height queries —
   * the visual mesh (built by {@link buildGeometry}) stays flat regardless.
   */
  setHeightGrid(grid: HeightGrid | null): void {
    this.heightGrid = grid && grid.coverage > 0 ? grid : null;
  }

  /** Bilinearly interpolated real elevation at (east, north), or `null` with no data there. */
  heightAt(east: number, north: number): number | null {
    const grid = this.heightGrid;
    if (!grid) return null;
    const { minX, minY, maxX, maxY } = this.bbox;
    if (east < minX || east > maxX || north < minY || north > maxY) return null;

    const points = grid.cells + 1;
    const fx = ((east - minX) / (maxX - minX)) * grid.cells;
    const fy = ((maxY - north) / (maxY - minY)) * grid.cells;
    const x0 = Math.min(grid.cells - 1, Math.max(0, Math.floor(fx)));
    const y0 = Math.min(grid.cells - 1, Math.max(0, Math.floor(fy)));
    const tx = Math.min(1, Math.max(0, fx - x0));
    const ty = Math.min(1, Math.max(0, fy - y0));

    const v00 = grid.values[y0 * points + x0];
    const v10 = grid.values[y0 * points + x0 + 1];
    const v01 = grid.values[(y0 + 1) * points + x0];
    const v11 = grid.values[(y0 + 1) * points + x0 + 1];
    const present = [v00, v10, v01, v11].filter(Number.isFinite);
    if (present.length === 0) return null;
    // A missing corner (edge of the surveyed area) falls back to the average of the rest
    // rather than a full bilinear blend against a value that doesn't exist.
    if (present.length < 4) return present.reduce((a, b) => a + b, 0) / present.length;

    const top = v00 + (v10 - v00) * tx;
    const bottom = v01 + (v11 - v01) * tx;
    return top + (bottom - top) * ty;
  }

  /**
   * Applies the shared low-resolution texture that covers the whole dataset.
   *
   * The caller passes a `clone()` of the base texture: clones share one GPU upload but
   * carry their own `offset`/`repeat`, which is what lets every tile sample its own
   * sub-rectangle of a single image.
   */
  setBaseTexture(clone: Texture, uv: UvRect): void {
    if (this.baseTexture && this.baseTexture !== clone) this.baseTexture.dispose();
    this.baseTexture = clone;
    applyUv(clone, uv);
    if (!this.ownTexture) this.applyMap(clone, -1, 0);
  }

  /** Applies a texture read specifically for this tile at `level`. */
  setDetailTexture(texture: Texture, uv: UvRect, level: number, size: number): void {
    if (this.ownTexture && this.ownTexture !== texture) this.ownTexture.dispose();
    this.ownTexture = texture;
    applyUv(texture, uv);
    this.applyMap(texture, level, size);
    this.textureCost = textureBytes(texture);
  }

  /** Drops this tile's dedicated texture and falls back to the shared base image. */
  releaseDetail(): void {
    if (!this.ownTexture) return;
    this.ownTexture.dispose();
    this.ownTexture = null;
    this.textureCost = 0;
    if (this.baseTexture) this.applyMap(this.baseTexture, -1, 0);
  }

  private applyMap(texture: Texture, level: number, size: number): void {
    const material = this.mesh.material;
    material.map = texture;
    material.needsUpdate = true;
    this.textureLevel = level;
    this.textureSize = size;
  }

  setOpacity(opacity: number): void {
    const material = this.mesh.material;
    material.opacity = opacity;
    material.transparent = opacity < 1;
    material.needsUpdate = true;
  }

  setWireframe(enabled: boolean): void {
    this.mesh.material.wireframe = enabled;
  }

  cancel(): void {
    this.abort?.abort();
    this.abort = null;
    this.loading = false;
  }

  dispose(): void {
    this.cancel();
    this.mesh.geometry.dispose();
    this.mesh.material.dispose();
    this.ownTexture?.dispose();
    this.ownTexture = null;
    this.baseTexture?.dispose();
    this.baseTexture = null;
  }
}

function applyUv(texture: Texture, uv: UvRect): void {
  texture.offset.set(uv.offsetX, uv.offsetY);
  texture.repeat.set(uv.repeatX, uv.repeatY);
}
